/**
 * @fileoverview Column show/hide menu for the bank transactions tables.
 *
 * Purpose:
 * - Lets the practitioner toggle which columns are visible on the hand-rolled
 *   "All Transactions" and "Documents → Accounts" tables (which aren't backed
 *   by TanStack React Table, so the generic `ColumnVisibilityMenu` can't be
 *   used).
 * - Choices persist per table in `localStorage` so a practitioner's preferred
 *   layout survives reloads.
 *
 * Mirrors the checkbox popover chrome of `ui/composed/data-display/
 * column-visibility-menu` and the "Columns N/M" trigger used across the app.
 */

import { useState } from "react";
import { Check, Columns3 } from "lucide-react";
import { Badge } from "@/ui/primitives/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/ui/composed/overlays/popover";
import { cn } from "@/utils/misc";

export interface ColumnOption {
  /** Stable key used for visibility tracking + conditional cell rendering. */
  key: string;
  /** Human-readable label shown in the menu. */
  label: string;
}

/**
 * "Columns" popover with a checkbox per toggleable column.
 *
 * @component
 * @example
 * <ColumnsMenu columns={COLS} hidden={hidden} onToggle={toggle} />
 */
export function ColumnsMenu({
  columns,
  hidden,
  onToggle,
  className,
}: {
  columns: ColumnOption[];
  hidden: Set<string>;
  onToggle: (key: string) => void;
  className?: string;
}) {
  const [open, setOpen] = useState(false);
  const visibleCount = columns.filter((c) => !hidden.has(c.key)).length;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          className={cn(
            "inline-flex h-8 items-center gap-1.5 rounded-lg border border-border/60 bg-background px-2.5 text-xs text-muted-foreground transition hover:bg-secondary hover:text-foreground",
            className,
          )}
        >
          <Columns3 className="size-3.5" />
          Columns
          <Badge variant="secondary" className="ml-0.5 text-[10px]">
            {visibleCount}/{columns.length}
          </Badge>
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-1">
        {columns.map((col) => {
          const visible = !hidden.has(col.key);
          return (
            <button
              key={col.key}
              type="button"
              onClick={() => onToggle(col.key)}
              className="flex w-full items-center gap-2 rounded px-2 py-1.5 text-sm transition hover:bg-secondary"
            >
              <span
                className={cn(
                  "flex size-4 shrink-0 items-center justify-center rounded border",
                  visible
                    ? "border-primary bg-primary text-primary-foreground"
                    : "border-border bg-background",
                )}
              >
                {visible && <Check className="size-3" />}
              </span>
              <span className="text-primary/80">{col.label}</span>
            </button>
          );
        })}
      </PopoverContent>
    </Popover>
  );
}
